import { memo, useRef } from 'react';
import type { Cell, Row } from '@tanstack/react-table';
import type { Control, FieldValues } from 'react-hook-form';
import { Box, TableCell as MuiTableCell } from '@mui/material';
import { KeyboardArrowRight, KeyboardArrowDown } from '@mui/icons-material';
import type { DataSchema, Schema } from 'types';
import { TABLE_CELL_TYPE } from 'types/enums.ts';
import { createEditableComponentFactory, getFormFieldName } from './editableComponentFactory.tsx';
import { isMobileApp } from 'utils/common.ts';
import { getCommonPinningStyles } from './tableFunctions';
import type { TableColumnAggregationState } from 'utils/table.ts';
import type { FormItemUiDefinition } from 'types/formItem.ts';

interface Props<T extends keyof P, P extends DataSchema, K extends FieldValues> {
  agendaId?: string;
  cell: Cell<Schema<T, P>, unknown>;
  entireRowClicked?: (row: Schema<T, P>) => void;
  row: Row<Schema<T, P>>;
  dataSchema: P;
  control: Control<K>;
  isNew?: boolean;
  formItemUiDefinition?: FormItemUiDefinition | null;
  /**
   * This prop is needed to trigger a re-render when column aggregation changes.
   * It is not used in the function body, but its presence in the props object ensures that
   * React.memo detects a change and refreshes grouped/aggregated cell output.
   */
  columnAggregation?: TableColumnAggregationState;
}

type ColumnMeta<P extends DataSchema> = {
  processedSchema: P;
  uiDefinition?: FormItemUiDefinition;
};

const TableCellEditBase = <T extends keyof P, P extends DataSchema, K extends FieldValues>({
  agendaId,
  cell,
  entireRowClicked,
  row,
  dataSchema,
  control,
  isNew,
  formItemUiDefinition,
}: Props<T, P, K>) => {
  const cellRef = useRef<HTMLElement | null>(null);
  const mobileApp = isMobileApp();
  const processedSchema = (cell.column.columnDef.meta as ColumnMeta<P>).processedSchema;
  const rowDataSchema = dataSchema[cell.column.id] ?? processedSchema?.[cell.column.id];

  const type = rowDataSchema.type ?? TABLE_CELL_TYPE.TEXT;
  const registerName = getFormFieldName<T, P, K>(row, cell.column.id);

  const isGrouped = cell.getIsGrouped();
  const isAggregated = cell.getIsAggregated();
  const isPlaceholder = cell.getIsPlaceholder();
  const isRowExpanded = row.getIsExpanded();
  const isGroupingActive = cell.getContext().table.getState().grouping.length > 0;

  const handleRowExpand = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.stopPropagation();
    row.getToggleExpandedHandler()();
  };

  const GroupedContent = () => {
    const value = cell.getValue();
    const displayValue = Array.isArray(value)
      ? value.map((v) => (typeof v === 'object' && v !== null && 'value' in v ? v.value : String(v))).join('; ')
      : value;

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', cursor: row.getCanExpand() ? 'pointer' : 'normal' }} onClick={handleRowExpand}>
        {isRowExpanded ? <KeyboardArrowDown fontSize="small" sx={{ mr: 0.5 }} /> : <KeyboardArrowRight fontSize="small" sx={{ mr: 0.5 }} />}
        {String(displayValue ?? '')} <p style={{ marginLeft: '6px' }}>({row.subRows.length})</p>
      </Box>
    );
  };

  const renderCell = () => {
    switch (true) {
      case isGrouped:
        return <GroupedContent />;
      case isPlaceholder:
        return null;
      default:
        return createEditableComponentFactory<T, P, K>({
          agendaId,
          type,
          row,
          rowDataSchema,
          control,
          registerName,
          formItemUiDefinition,
          background: mobileApp && isNew ? 'white' : 'transparent',
          disabled: isGroupingActive && isAggregated,
        });
    }
  };

  return (
    <MuiTableCell
      ref={cellRef}
      sx={{
        ...getCommonPinningStyles(cell.column, { id: cell.row.id }),
        ...(mobileApp && isNew ? { display: 'block', padding: 0, border: 0, width: '100%' } : {}),
      }}
      onClick={(e) => {
        e.stopPropagation();

        if (e.target === cellRef.current) {
          entireRowClicked?.(row.original);
        }
      }}>
      {renderCell()}
    </MuiTableCell>
  );
};

export const TableCellEdit = memo(TableCellEditBase) as unknown as typeof TableCellEditBase;
